import React, {FC, useEffect, useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import classNames from 'classnames';
import Button from 'components/Button';
import {getRecentBooking} from 'components/ContentPagesComponents/TextHeader';
import {useAppointments} from 'components/ContentPagesComponents/TextHeader/query';
import {Appointment} from 'components/ContentPagesComponents/TextHeader/query';
import {useToggle} from 'hooks/useToggle';
import {isEmpty, omit} from 'lodash';
import {cookies, CookKey} from 'utils/cookies';
import {hasWindow} from 'utils/functions';
import {getLocalId} from 'utils/localId';
import {Endpoints, postValues} from 'utils/n8n';
import {useTxns} from 'utils/txns/query';

import Dots from './svgs/dots.inline.svg';
import {SurveyOption} from './surveyOptions';
import {surveyOptions} from './surveyOptions';
import {ToggleSmooth} from './ToggleSmooth';

import * as styles from './styles.module.scss';

enum CustomerType {
	new = 'New',
	repeat = 'Repeat'
}

type SliderButtonProps = {
	text: string;
	toggler: SingleToggle;
	icon: React.ReactNode;
}

type SingleToggle = {
	open: boolean;
	toggle: () => void;
}

const SliderButton: FC<SliderButtonProps> = ({text, toggler, icon}) => (
	<div onClick={toggler.toggle}
		className={classNames({
			[styles.sliderContainer]: true,
			[styles.selected]: toggler.open,
		})}>
		<div className={styles.label}>
			{icon}
			<span>{text}</span>
		</div>
		<ToggleSmooth
			open={toggler.open}
			onClick={toggler.toggle} />
	</div>
);

type ExitSurveyModalProps = {
	showKey?: string;
}

type LocationInfo = {
	location: string | undefined;
	region: string | undefined;
}

const shuffle = <T, >(inputArray: T[]): T[] => {
	let currentIndex = inputArray.length;
	let randomIndex;
	let tempVal;

	// Fisher–Yates shuffle
	while (currentIndex !== 0) {
		randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex--;
		tempVal = inputArray[currentIndex];
		inputArray[currentIndex] = inputArray[randomIndex];
		inputArray[randomIndex] = tempVal;
	}

	return inputArray;
};

const onClick = (
	values: Record<string, boolean>,
	locationInfo: LocationInfo,
	userID: string | undefined,
	reason: string | undefined,
	customerType: string | undefined,
) => {
	if (isEmpty(values) || userID === 'notset') {
		return;
	}
	postValues(Endpoints.exitSurvey, {
		user: getLocalId(),
		selected: values,
		reason,
		location: locationInfo?.location,
		region: locationInfo?.region,
		userID,
		customerType,
	});
};

const prevSeen = (key: string): boolean => {
	return Boolean(key && cookies.get(CookKey.ExitSurvey) === key);
};

export const ExitSurveyModal: FC<ExitSurveyModalProps> = ({showKey: key}) => {
	const {data} = useAppointments();
	const {txns, fetchMore} = useTxns();
	const userID = data?.auth.id;
	let recentBooking: Appointment | undefined;
	let locationInfo: LocationInfo;
	let customerType: string | undefined;

	// txns are pulled in 'pages' of 50, we continue to fetch pages until
	// there are no remaining pages, at which point the txns object is complete
	useEffect(() => {
		if (fetchMore) {
			fetchMore();
		}
	}, [txns]);

	if (data?.auth.user?.appts) {
		recentBooking = getRecentBooking(data?.auth.user?.appts);
		locationInfo = {
			location: recentBooking?.details.location.details.name,
			region: recentBooking?.details.location.regions.primary,
		};

		const purchases = txns.filter((txn) =>
			txn.details.reason.includes('Purchase') || txn.details.reason.includes('Gift Code'));
		customerType = CustomerType.repeat;
		if (purchases.length <= 1) {
			customerType = CustomerType.new;
		}
	}

	const [shuffled, setShuffled] = useState<SurveyOption[]>([]);
	const {open, toggle} = useToggle(false);
	const [reason, setReason] = useState<string>('');

	useEffect(() => {
		setShuffled([
			...shuffle(surveyOptions),
			{
				text: 'Other',
				icon: <Dots />
			},
		]);
		if (!key) {
			return;
		} else if (!hasWindow() || prevSeen(key)) {
			return;
		}
		cookies.set(CookKey.ExitSurvey, key, {expires: 1});
		if (!open) toggle();
	  }, []);

	const [values, setValues] = useState<Record<string, boolean>>({});
	const singleToggle = (key: string) => {
		return {
			open: values[key],
			toggle: () => {
				if (values[key]) {
					setValues(omit(values, key));
				} else {
					setValues({
						...values,
						[key]: true,
					});
				}
			}
		};
	};

	return !open ? null : (
		<Modal scrollable show size='lg' onHide={toggle}>
			<Modal.Header closeButton className={styles.header}>
				<h2>This quick answer has a <em>huge</em> impact.</h2>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				<span>
					How did you find us? Your answer helps us make scans
					more accessible to the people who need it most.
				</span>
				<div className={styles.toggles}>
					{shuffled.map((source, index) => (
						<SliderButton
							key={index}
							text={source.text}
							toggler={singleToggle(source.text)}
							icon={source.icon} />
					))}
				</div>
				{singleToggle('Other').open &&
					<input
						className={styles.input}
						type='text'
						placeholder='Please specify how you found us'
						onChange={(e) => setReason(e.target.value)} />}
				<Button
					variant='primary'
					onClick={() => {
						if (!isEmpty(values)) {
							onClick(values, locationInfo, userID, reason, customerType);
						}
						toggle();
					}}
					className={styles.submit}>
					Submit
				</Button>
			</Modal.Body>
		</Modal>
	);
};

export default ExitSurveyModal;
